import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import defaultCoverImage from "./download.jpg";
import "./Modal.css";

const Readjournal = () => {
  const [journals, setJournals] = useState([]);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [journalToDelete, setJournalToDelete] = useState(null);
  const { username } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchJournals = async () => {
      try {
        const response = await axios.get(
          `https://happyu.onrender.com/${username}/journals`
        );
        setJournals(response.data);
      } catch (error) {
        console.error("Error fetching journals:", error);
      }
    };
    fetchJournals();
  }, [username]);

  const shortenText = (text, maxLength) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substr(0, maxLength) + "...";
  };

  const handleEdit = (journalId) => {
    navigate(`/${username}/journals/${journalId}/edit`);
  };

  const handleDelete = (journalId) => {
    setJournalToDelete(journalId);
    setShowConfirmationModal(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(
        `https://happyu.onrender.com/journal-delete/${username}/${journalToDelete}`
      );
      setJournals(
        journals.filter((journal) => journal._id !== journalToDelete)
      );
      setShowConfirmationModal(false);
    } catch (error) {
      console.error("Error deleting journal:", error);
    }
  };

  const closeModal = () => {
    setShowConfirmationModal(false);
  };

  return (
    <div className="bg-gradient-to-b from-sky-100 via-teal-50 to-white py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 lg:px-8 text-center">
        <div className="w-full text-center mb-10">
          <h2 className="text-4xl font-extrabold tracking-tight text-teal-700 sm:text-4xl mb-2 text-center">
            Your Journals
          </h2>
          <p className="text-lg leading-8 text-gray-500 text-center">
            Keep your memorable days stored with you!
          </p>
        </div>

        <div className="mx-auto mt-6 grid max-w-2xl grid-cols-1 gap-8 sm:mt-10 sm:gap-y-12 lg:mx-0 lg:max-w-none lg:grid-cols-3">
          {journals.length === 0 ? (
            <div className="col-span-3 text-center text-gray-400 py-16">
              No journals yet.{" "}
              <span className="text-teal-700 font-medium">
                Start your first journal!
              </span>
            </div>
          ) : (
            journals.map((journal) => {
              const coverImage = journal.coverPicture
                ? `https://happyu.onrender.com/${journal.coverPicture}`
                : defaultCoverImage;

              return (
                <div
                  key={journal._id}
                  className="group bg-white rounded-3xl shadow-lg border border-teal-100 overflow-hidden flex flex-col h-full relative hover:-translate-y-2 hover:shadow-2xl transition-all duration-300"
                >
                  <div className="relative">
                    <img
                      src={coverImage}
                      alt="Cover"
                      className="h-40 w-full object-cover object-center transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-white/80 via-teal-50/40 to-transparent pointer-events-none"></div>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-xs text-gray-400">
                        {new Date(journal.createdAt).toLocaleDateString()}
                      </span>
                      <div className="flex flex-wrap gap-1">
                        {journal.tags &&
                          journal.tags.map((tag, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center rounded-xl bg-gradient-to-r from-teal-200 to-indigo-100 px-2 py-0.5 text-xs font-medium text-teal-900 shadow"
                            >
                              #{tag}
                            </span>
                          ))}
                      </div>
                    </div>
                    <h3 className="text-[1.2rem] font-bold text-teal-800 mb-2">
                      <Link
                        to={`/${username}/readjournals/${journal._id}`}
                        className="hover:underline hover:text-sky-500 transition " 
                      >
                        {journal.title}
                      </Link>
                    </h3>
                    <p className="line-clamp-3 text-[1rem] text-gray-700 mb-6 break-words">
                      {shortenText(journal.article, 120)}
                    </p>
                    <div className="mt-auto flex items-center gap-2 justify-end">
                      <button
                        className="bg-teal-500 text-white px-4 py-1.5 rounded-xl text-sm font-semibold hover:bg-sky-500 shadow transition-all duration-200"
                        onClick={() => handleEdit(journal._id)}
                      >
                        Edit
                      </button>
                      <button
                        className="bg-red-500 text-white px-4 py-1.5 rounded-xl text-sm font-semibold hover:bg-red-600 shadow transition-all duration-200"
                        onClick={() => handleDelete(journal._id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  {/* Decorative Glow/Shadow */}
                  <div className="absolute -bottom-3 left-8 right-8 h-5 rounded-xl bg-gradient-to-r from-teal-100 via-white to-indigo-100 opacity-60 blur"></div>
                </div>
              );
            })
          )}
        </div>
      </div>
      {showConfirmationModal && (
        <div className="modal-overlay flex items-center justify-center fixed inset-0 bg-black/40 z-50">
          <div className="modal-content bg-white rounded-2xl shadow-xl p-8 max-w-sm w-full">
            <p className="text-lg font-semibold mb-5 text-center text-teal-700">
              Are you sure you want to delete this journal?
            </p>
            <div className="flex justify-center gap-4">
              <button
                className="bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600 focus:ring-2 focus:ring-red-800 transition"
                onClick={confirmDelete}
              >
                Delete
              </button>
              <button
                className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-300 transition"
                onClick={closeModal}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Readjournal;
